import 'antd-mobile/es/global';

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { ErrorBlock } from 'antd-mobile';
import get from 'lodash/get';
import styled from 'styled-components';
import { getUserSelf } from '../api';
import Navigation from './Navigation';
import Task from './Task';
import WorkOrders from './WorkOrders';
import WorkOrder from './WorkOrder';
import Defect from './Defect';
import Profile from './Profile';
import { matchesPermission } from '../utils/permission-helpers';
import IssuedTools from './IssuedTools';
import AccessPanel from './AccessPanel';
import UserSelfContext from '../utils/self-context';

const appHeight = () => {
	const doc = document.documentElement;
	doc.style.setProperty('--app-height', `${window.innerHeight}px`);
};

const MainContent = styled.div`
	height: var(--app-height);
	--adm-color-primary: #376BFB;
	display: flex;
	flex-flow: column;
	background: #f0f2f5;
  	flex-direction: column;
`;

const Body = styled.div`
	flex: 1;
	overflow: auto;
	justify-content: center;
	align-items: center;
`;

function App() {
	const [userSelf, setUserSelf] = useState({});
	const [loading, setloading] = useState(true);
	const [err, setErr] = useState(false);

	async function fetchSelf(silent) {
		if (!silent) {
			setloading(true);
		}

		try {
			const selfData = await getUserSelf();
			setUserSelf(selfData);
		} catch (e) {
			if (get(e.response, 'status') !== 403) {
				setErr(true);
			}
		}

		setloading(false);
	}

	useEffect(() => {
		fetchSelf();

		window.addEventListener('resize', appHeight);
		appHeight();
	}, []);

	function accessDenied() {
		return <ErrorBlock
			title="Access denied"
			description="You dont have permissions to view this page"
			status="empty"
		/>;
	}

	function redirectToLogin() {
		window.location.href = `${process.env.API_URL}/login?redirect=${process.env.APP_URL}`;
	}

	if (err) {
		return <ErrorBlock title="Oops, something wrong" description={'Please try to refresh page or try again later'} fullPage status="disconnected" />;
	}

	if (loading) {
		return <ErrorBlock title="Loading..." description={null} fullPage status="loading" />;
	}

	if (!loading && !userSelf.id) {
		redirectToLogin();
		return null;
	}

	return (userSelf.id ? <Router>
		<UserSelfContext.Provider value={{ userSelf, fetchSelf }}>
			<MainContent>
				<Body>
					<Switch>
						<Route
							exact
							path='/'
							authed={(!!userSelf.id && !loading)}
						>
							{matchesPermission(userSelf, 'view_work_orders') ? <WorkOrders /> : accessDenied()}
						</Route>
						<Route
							exact
							path='/issued-tools'
							authed={(!!userSelf.id && !loading)}
						>
							{matchesPermission(userSelf, 'view_tools') ? <IssuedTools /> : accessDenied()}
						</Route>
						<Route
							exact
							path='/work-order/:id'
							authed={(!!userSelf.id && !loading)}
						>
							{matchesPermission(userSelf, 'view_work_orders') ? <WorkOrder /> : accessDenied()}
						</Route>
						<Route
							exact
							path='/work-order/:workOrderId/task/:id'
							authed={(!!userSelf.id && !loading)}
						>
							{matchesPermission(userSelf, 'view_work_orders') ? <Task /> : accessDenied()}
						</Route>
						<Route
							exact
							path='/work-order/:workOrderId/defect/:id'
							authed={(!!userSelf.id && !loading)}
						>
							{matchesPermission(userSelf, 'view_work_orders') ? <Defect /> : accessDenied()}
						</Route>
						<Route
							exact
							path='/work-order/:workOrderId/access-panel/:id'
							authed={(!!userSelf.id && !loading)}
						>
							{matchesPermission(userSelf, 'view_work_orders') ? <AccessPanel /> : accessDenied()}
						</Route>
						<Route exact path='/profile' authed={(!!userSelf.id && !loading)}>
							<Profile />
						</Route>
						<Route
							render={() => (
								<ErrorBlock status='empty' description={null} title="Sorry, this page dos not exist" />
							)}
						/>
					</Switch>
				</Body>
				{!!userSelf.id && !loading && <Navigation />}
			</MainContent>
		</UserSelfContext.Provider>
	</Router> : <ErrorBlock title="Loading..." description={null} fullPage status="loading" />);
}

export default App;
